import * as EXIF from 'exif-js/exif';
import { t } from 'i18next';

import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { GlobalHelper } from '../utils/GlobalHelper';
import { Stats, StatsEventAction } from '../utils/Stats';
import { ProjectSettingsActions } from './ProjectSettingsActions';
import { FloorPlanManager } from '../utils/FloorPlanManager';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { FloorPlan } from '../utils/FloorPlan';
import { FloorPlanStore } from '../stores/FloorPlanStore';
import { UIHelper } from '../utils/UIHelper';
import { ViewSettingsActions } from './ViewSettingsActions';
import { FLOOR_IMAGE_MAXSIZE, FLOOR_IMAGE_MAXWIDTH, FLOOR_IMAGE_MAXHEIGHT } from '../constants/FloorConstants';
import { VisibilityActions } from './VisibilityActions';
import { Overlay } from '../constants/Enums';
import { MouseCoords, CropPosition } from '../components/overlay/floorplan/FloorPlanSubPage';

// tslint:disable-next-line:no-any
declare var MegaPixImage: any;

const FloorPlanDefaultOpacity: number = 0.5;
const FloorPlanDefaultColorReduction: number = 0.24;
const FloorPlanDefaultBrightness: number = 1;

export interface FloorPlanSetting {
    angle: number;
}

export abstract class FloorPlanActions {
    public static reset(): void {
        ProjectSettingsActions.hideFloorPlan();
        if (GlobalHelper.floorPlan != null && GlobalHelper.floorPlan.setTexture != null) {
            GlobalHelper.floorPlan.setTexture(null);
            FloorPlanManager.reset();
        }
    }

    public static setBrightness(value: number): void {
        GlobalHelper.floorPlan.brightness = value;
        GlobalHelper.coreApp.viewer.requestRenderFrame();
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_BRIGHTNESS,
            value: value
        });
    }

    public static setColorReduction(value: number): void {
        GlobalHelper.floorPlan.colorReduction = value;
        GlobalHelper.coreApp.viewer.requestRenderFrame();
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_COLOR_REDUCTION,
            value: value
        });
    }

    public static setVisibility(value: number): void {
        GlobalHelper.floorPlan.visibility = value;
        GlobalHelper.coreApp.viewer.requestRenderFrame();
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_OPACITY,
            value: value
        });
    }

    public static setRatio(value: number): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_RATIO,
            value: value
        });
    }

    public static setImageId(value: string): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_IMAGE_ID,
            value: value
        });
    }

    public static setFloorPlanImage(value: File): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_IMAGE,
            value: value
        });
    }

    public static setAngle(value: number): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_ANGLE,
            value: value
        });
    }

    public static setup(value: FloorPlanSetting): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SETUP_FLOORPLAN,
            value: value
        });
    }

    public static setFloorPlan(
        originalImage: HTMLImageElement,
        relativeImagePosition: MouseCoords,
        cropPosition: CropPosition,
        zoom: number,
        rotation: number
    ): Promise<void> {
        if (!AppConfigurationStore.getState().floorplanAvailable) {
            return Promise.reject(new Error('Floor plan is not available'));
        }
        return new Promise<void>((resolve: () => void, reject: (error: Error) => void): void => {
            if (originalImage != null) {
                const cropImageCanvas: HTMLCanvasElement = document.createElement('canvas') as HTMLCanvasElement;
                const croppedImageWidth: number = Math.abs(cropPosition.x1 - cropPosition.x2) / zoom;
                const croppedImageHeight: number = Math.abs(cropPosition.y1 - cropPosition.y2) / zoom;
                let scale: number = 1;
                if (Math.max(croppedImageWidth, croppedImageHeight) > FloorPlan.MAXSIDELENGTH) {
                    scale = FloorPlan.MAXSIDELENGTH / Math.max(croppedImageWidth, croppedImageHeight);
                }
                // croppedImageWidth = croppedImageWidth * scale;
                // croppedImageHeight = croppedImageHeight * scale;
                const croppedImagePosX: number = -Math.min(cropPosition.x1, cropPosition.x2) / zoom;
                const croppedImagePosY: number = -Math.min(cropPosition.y1, cropPosition.y2) / zoom;
                const imagePosX: number = (relativeImagePosition.x) / zoom;
                const imagePosY: number = (relativeImagePosition.y) / zoom;
                cropImageCanvas.width = croppedImageWidth * scale;
                cropImageCanvas.height = croppedImageHeight * scale;
                const rotatedImageContext: CanvasRenderingContext2D = cropImageCanvas.getContext('2d')!;
                const angleSine: number = Math.sin(rotation / (180 / Math.PI));
                const angleCosine: number = Math.cos(rotation / (180 / Math.PI));
                rotatedImageContext.fillStyle = '#ffffff';
                rotatedImageContext.fillRect(0, 0, cropImageCanvas.width, cropImageCanvas.height);
                rotatedImageContext.setTransform(
                    angleCosine,
                    angleSine,
                    -angleSine,
                    angleCosine,
                    (imagePosX + croppedImagePosX) * scale,
                    (imagePosY + croppedImagePosY) * scale
                );
                rotatedImageContext.drawImage(
                    originalImage,
                    0,
                    0,
                    originalImage.width * scale,
                    originalImage.height * scale
                );

                const width: number = (croppedImageWidth) / FloorPlanStore.getState().ratio;
                const height: number = (croppedImageHeight) / FloorPlanStore.getState().ratio;

                const rotatedImageDataUrl: string = cropImageCanvas.toDataURL();
                FloorPlanActions.setFloorPlanTexture(
                    rotatedImageDataUrl,
                    width,
                    height
                );

                // reset floor plan settings
                FloorPlanActions.setVisibility(FloorPlanDefaultOpacity);
                FloorPlanActions.setColorReduction(FloorPlanDefaultColorReduction);
                FloorPlanActions.setBrightness(FloorPlanDefaultBrightness);

                // FixMe: error message
                UIHelper.ignoreReject(
                    FloorPlanManager.updateTexture(
                        rotatedImageDataUrl
                    )
                );

                ProjectSettingsActions.showFloorPlan();
                resolve();
            } else {
                reject(new Error('Image is null'));
            }
        });
    }

    public static setImageOrientation(value: number): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_ORIENTATION,
            value: value
        });
    }

    public static getImageFile(): void {
        const tempFileInput: HTMLElement | null = document.getElementById('temporary-file-input');
        if (tempFileInput != null) {
            tempFileInput.remove();
        }
        let fileInput: HTMLInputElement | null = document.createElement('input');
        fileInput.id = 'temporary-file-input';
        fileInput.style.display = 'none';
        fileInput.accept = 'image/png,image/jpeg';
        document.body.appendChild(fileInput);
        fileInput.type = 'file';
        fileInput.onerror = () => {
            fileInput = null;
        };
        fileInput.addEventListener(
            'change',
            (event: Event) => {
                const target: HTMLInputElement = event.target as HTMLInputElement;
                if (target.files != null && target.files.length) {
                    if (/^image\//.test(target.files[0].type)) {
                        FloorPlanActions.loadImage(target.files[0]);
                    } else {
                        UIHelper.showMessage(t('This file can not be opened. You can open image files only.'));
                    }
                }
            },
            false
        );
        fileInput.click();
    }

    public static setFloorPlanTexture(data: string, width: number, height: number, zoomToFit: boolean = true): void {
        GlobalHelper.floorPlan.setTexture(data);
        GlobalHelper.floorPlan.setSize(
            width,
            height,
            zoomToFit
        );

        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_FLOORPLAN_SIZE,
            value: {
                width: width,
                height: height
            }
        });
    }

    protected static loadImage(imageFile: File): void {
        ViewSettingsActions.withLock<void>((): Promise<void> => {
            return new Promise<void>((resolve, reject) => {
                if (imageFile.size > FLOOR_IMAGE_MAXSIZE) {
                    UIHelper.showMessage(t('The image file is too large. Please load a smaller file.'));
                    resolve();
                } else {
                    const fr: FileReader = new FileReader();
                    fr.onerror = () => {
                        UIHelper.showMessage(t('This file can not be opened.'));
                        fr.onerror = null;
                        fr.onload = null;
                        resolve();
                    };
                    fr.onload = () => {
                        fr.onerror = null;
                        fr.onload = null;
                        FloorPlanStore.image.onerror = () => {
                            UIHelper.showMessage(t('This file can not be opened.'));
                            FloorPlanStore.image.onerror = null;
                            FloorPlanStore.image.onload = null;
                            resolve();
                        };
                        FloorPlanStore.image.onload = (): void => {
                            FloorPlanStore.image.onerror = null;
                            FloorPlanStore.image.onload = null;
                            if (FloorPlanStore.image.width > FLOOR_IMAGE_MAXWIDTH || FloorPlanStore.image.height > FLOOR_IMAGE_MAXHEIGHT) {
                                UIHelper.showMessage(t('The image file is too large. Please load a smaller file.'));
                                resolve();
                            } else {
                                try {
                                    EXIF.getData(imageFile as any, () => {
                                        const orientation: number = EXIF.getTag(imageFile, 'Orientation');
                                        FloorPlanActions.setImageOrientation(orientation);
                                        // tslint:disable-next-line:no-any
                                        const mpi: any = new MegaPixImage(imageFile);
                                        FloorPlanStore.image.onload = () => {
                                            VisibilityActions.changeOverlay(Overlay.FLOORPLAN);
                                            FloorPlanActions.setImageId(UIHelper.getUUID());
                                            resolve();
                                        };
                                        mpi.render(FloorPlanStore.image);
                                    });
                                } catch (e) {
                                    UIHelper.showMessage(t('This file can not be opened.'));
                                    resolve();
                                } finally {
                                    FloorPlanStore.image.onerror = null;
                                    FloorPlanStore.image.onload = null;
                                }
                            }
                        };
                        FloorPlanStore.image.src = fr.result as string;
                    };
                    fr.readAsDataURL(imageFile);
                }
            });
        });
    }

    protected static sendEvent(action: StatsEventAction, label?: string): void {
        Stats.sendEvent({
            category: 'FloorPlan',
            action,
            label
        });
    }

    protected static resetImageListener(): void {
        FloorPlanStore.image.onerror = null;
        FloorPlanStore.image.onload = null;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/FloorPlanActions.ts